import React, { useMemo, useState } from 'react';
import { DynamicHealthPlanStep } from '../../Interfaces/DynamicHealthPlanStep.interface';
import { DynamicHealthPlanStepType } from '../../Enums/DynamicHealthPlanStepType.enum';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import DynamicForm from '../DynamicForm/DynamicForm';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import { useAppContext } from '../../../../Services/AppContext';
import EducationMaterials from '../../../../Components/EducationMaterials/EducationMaterials';
import PatientsQuestionnaires from '../../../Questionnaire/Components/PatientsQuestionnaires/PatientsQuestionnaires';
import { QuestionnaireModule } from '../../../Questionnaire/Enums/QuestionnaireModule.enum';
import HealthPlanRequirementsWrapper from '../../../HealthPlan/Components/HealthPlanRequirements/HealthPlanRequirements';
import DynamicHealthPlanKDARConfirmationModal from './DynamicHealthPlanKDARConfirmationModal';
import useScope from '../../../Scope/Hooks/useScope';

interface Props {
	structure: DynamicHealthPlan;
	patientId: string;
	healthPlanCode: HealthPlanCode;
}

interface DynamicHealthPlan {
	name: string;
	steps: DynamicHealthPlanStep[];
	settings?: {
		initialStep?: string;
		canSkipSteps?: boolean;
		showStepsNumbers?: boolean;
	};
}

const DynamicHealthPlanQuestionnaireModule: Record<
	HealthPlanCode,
	QuestionnaireModule
> = {
	[HealthPlanCode.BABY]: QuestionnaireModule.MEDDIBABY,
	[HealthPlanCode.DIABETES]: QuestionnaireModule.DIABETES,
	[HealthPlanCode.THERAPIST]: QuestionnaireModule.THERAPIST,
	[HealthPlanCode.KDAR]: QuestionnaireModule.KDAR,
	[HealthPlanCode.OBESITOLOGY]: QuestionnaireModule.OBESITOLOGY,
};

const DynamicHealthPlan: React.FC<Props> = ({
	structure,
	patientId,
	healthPlanCode,
}) => {
	const { workplaceId } = useAppContext();
	const { isKDARWorkplace } = useScope();
	const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);

	const onDynamicFormSuccess = () => {
		if (isKDARWorkplace) {
			setIsInvitationModalOpen(true);
		}
	};

	const onInvitationModalClose = () => {
		setIsInvitationModalOpen(false);
		if (isKDARWorkplace) {
			// @ts-ignore
			window.location.href = '/kartoteka-new';
		}
	};

	const steps: NavigationStepperItemType[] = useMemo(() => {
		const tempSteps: NavigationStepperItemType[] = [];

		structure.steps?.forEach(step => {
			const basicStepData = {
				key: step.key,
				name: step.name,
			};
			switch (step.type) {
				case DynamicHealthPlanStepType.FORM:
					tempSteps.push({
						...basicStepData,
						component: (params: NavigationStepperComponentParams) => (
							<DynamicForm
								wizardKey={
									step.settings?.wizardKey ? step.settings.wizardKey : ''
								}
								patientId={patientId}
								setIsLoading={params.setIsLoading}
								onSuccess={onDynamicFormSuccess}
							/>
						),
					});
					break;
				case DynamicHealthPlanStepType.EDUCATION_MATERIALS:
					tempSteps.push({
						...basicStepData,
						component: (params: NavigationStepperComponentParams) => (
							<EducationMaterials
								patientId={patientId}
								workplaceId={workplaceId}
							/>
						),
					});
					break;
				case DynamicHealthPlanStepType.QUESTIONNAIRES:
					tempSteps.push({
						...basicStepData,
						component: (params: NavigationStepperComponentParams) => (
							<PatientsQuestionnaires
								patientId={patientId}
								module={DynamicHealthPlanQuestionnaireModule[healthPlanCode]}
							/>
						),
					});
					break;
				case DynamicHealthPlanStepType.REQUIREMENTS:
					tempSteps.push({
						...basicStepData,
						component: (params: NavigationStepperComponentParams) => (
							<HealthPlanRequirementsWrapper
								healthPlanCode={healthPlanCode}
								patientId={patientId}
								setIsLoading={params.setIsLoading}
							/>
						),
					});
					break;
			}
		});

		return tempSteps;
	}, [structure, isKDARWorkplace]);

	return (
		<>
			{isInvitationModalOpen && (
				<DynamicHealthPlanKDARConfirmationModal
					patientId={patientId}
					onClose={onInvitationModalClose}
				/>
			)}
			<NavigationStepper
				steps={steps}
				initialStepPath={structure.settings?.initialStep}
				canSkip={structure.settings?.canSkipSteps}
				showStepNumbers={structure.settings?.showStepsNumbers}
			/>
		</>
	);
};

export default DynamicHealthPlan;
